import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useTranslation } from '../locales/translations';
import axios from 'axios';
import { MetricCard, Button, Modal, Card, Input, Select } from '../components/ui';

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [pendingFlags, setPendingFlags] = useState([]);
  const [pendingContent, setPendingContent] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Resource management state
  const [resources, setResources] = useState([]);
  const [resourceStats, setResourceStats] = useState(null);
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceFilters, setResourceFilters] = useState({
    resource_type: '',
    category: '',
    approval_status: '',
    search: ''
  });

  // User management state
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [userFilters, setUserFilters] = useState({
    search: '',
    role: '',
    active_only: true
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserDetails, setShowUserDetails] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [userPagination, setUserPagination] = useState({ skip: 0, limit: 20 });

  // Operation states for loading indicators
  const [deletingResources, setDeletingResources] = useState(new Set());
  const [publishingResources, setPublishingResources] = useState(new Set());

  // Toast notification state
  const [toast, setToast] = useState(null);
  
  // New state for better UX
  const [notification, setNotification] = useState(null);
  const [processingFlags, setProcessingFlags] = useState(new Set());
  const [debugMode, setDebugMode] = useState(true);

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ message, type, id: Date.now() });
    // Auto dismiss after 4 seconds
    setTimeout(() => setToast(null), 4000);
  };

  // Show notification (replacement for blocked alerts)
  const showNotification = (message, type = 'info') => {
    if (debugMode) console.log(`📢 NOTIFICATION [${type.toUpperCase()}]: ${message}`);
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Debug logger
  const debugLog = (category, message, data = null) => {
    if (debugMode) {
      console.log(`🔍 DEBUG [${category.toUpperCase()}]: ${message}`);
      if (data) {
        console.log('   Data:', data);
      }
    }
  };

  // Check if user is admin or moderator
  if (!user || (user.role !== 'admin' && user.role !== 'moderator')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('common.error')}</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchDashboardData();
    fetchPendingFlags();
    fetchPendingContent();
    if (activeTab === 'resources') {
      fetchResources();
      fetchResourceStats();
    }
    if (activeTab === 'users') {
      fetchUsers();
      fetchUserStats();
    }
  }, [activeTab, userFilters, userPagination]);

  const fetchDashboardData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/admin/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    }
  };

  const fetchPendingFlags = async () => {
    try {
      debugLog('fetch', 'Starting fetchPendingFlags request');
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/admin/flags`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        // Add timestamp to prevent caching
        params: {
          status_filter: 'pending',  // Fixed: changed from 'status' to 'status_filter' to match backend parameter
          _t: Date.now()
        }
      });
      
      debugLog('fetch', `Received ${response.data.length} flags from API`, {
        flagIds: response.data.map(f => `${f.id?.substring(0,8)}...`),
        statuses: response.data.map(f => f.status)
      });
      
      // Filter flags client-side as additional safety measure
      const actualPendingFlags = response.data.filter(flag => 
        flag.status === 'pending' && flag.is_active === true
      );
      
      debugLog('fetch', `After client-side filtering: ${actualPendingFlags.length} truly pending flags`);
      
      setPendingFlags(actualPendingFlags);
      console.log(`🔄 Refreshed pending flags: ${actualPendingFlags.length} flags loaded`);
      
    } catch (error) {
      debugLog('fetch', 'Error fetching pending flags', error);
      console.error('Error fetching pending flags:', error);
    }
  };

  const fetchPendingContent = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/admin/pending-content`);
      setPendingContent(response.data);
    } catch (error) {
      console.error('Error fetching pending content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Resource management functions
  const fetchResources = async () => {
    try {
      // Build query parameters with active_only filter
      const params = {
        ...resourceFilters,
        active_only: true  // Only fetch active (non-deleted) resources by default
      };
      
      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      console.log('📋 Fetching resources with params:', params);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/admin/resources`, { params });
      
      console.log(`✅ Fetched ${response.data.resources?.length || 0} active resources`);
      
      setResources(response.data.resources || []);
    } catch (error) {
      console.error('❌ Error fetching resources:', error);
      setError('Failed to fetch resources');
    }
  };

  const fetchResourceStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/admin/resources/stats`);
      setResourceStats(response.data);
    } catch (error) {
      console.error('Error fetching resource stats:', error);
    }
  };

  const createResource = async (resourceData) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${backendUrl}/api/admin/resources`, resourceData);
      if (response.status === 201) {
        fetchResources();
        fetchResourceStats();
        setShowResourceForm(false);
        return { success: true, message: 'Resource created successfully' };
      }
    } catch (error) {
      console.error('Error creating resource:', error);
      return { success: false, message: 'Failed to create resource' };
    }
  };

  const updateResource = async (resourceId, resourceData) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.put(`${backendUrl}/api/admin/resources/${resourceId}`, resourceData);
      fetchResources();
      setEditingResource(null);
      return { success: true, message: 'Resource updated successfully' };
    } catch (error) {
      console.error('Error updating resource:', error);
      return { success: false, message: 'Failed to update resource' };
    }
  };

  const deleteResource = async (resourceId) => {
    // Add loading state
    setDeletingResources(prev => new Set([...prev, resourceId]));
    
    try {
      console.log(`🗑️ Attempting to delete resource: ${resourceId}`);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.delete(`${backendUrl}/api/admin/resources/${resourceId}`);
      
      console.log('✅ Resource deletion successful:', response.data);
      
      // Show success toast
      showToast('Resource deleted successfully! The resource has been removed from the listing.', 'success');
      
      // Refresh data
      await fetchResources();
      await fetchResourceStats();
      
      return { success: true, message: 'Resource deleted successfully' };
    } catch (error) {
      console.error('❌ Error deleting resource:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Show specific error message to user
      let errorMessage = 'Failed to delete resource. Please try again.';
      
      if (error.response?.status === 404) {
        errorMessage = 'Resource not found. It may have already been deleted.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to delete this resource.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      // Show error toast
      showToast(errorMessage, 'error');
      
      return { success: false, message: errorMessage };
    } finally {
      // Remove loading state
      setDeletingResources(prev => {
        const newSet = new Set([...prev]);
        newSet.delete(resourceId);
        return newSet;
      });
    }
  };

  const publishResource = async (resourceId) => {
    // Add loading state
    setPublishingResources(prev => new Set([...prev, resourceId]));
    
    try {
      console.log(`📢 Publishing resource: ${resourceId}`);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.put(`${backendUrl}/api/admin/resources/${resourceId}/publish`);
      
      console.log('✅ Resource published successfully:', response.data);
      
      showToast('Resource published successfully! It is now visible to all users.', 'success');
      
      await fetchResources();
      await fetchResourceStats();
      
      return { success: true, message: 'Resource published successfully' };
    } catch (error) {
      console.error('❌ Error publishing resource:', error);
      
      let errorMessage = 'Failed to publish resource. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      showToast(errorMessage, 'error');
      return { success: false, message: errorMessage };
    } finally {
      // Remove loading state
      setPublishingResources(prev => {
        const newSet = new Set([...prev]);
        newSet.delete(resourceId);
        return newSet;
      });
    }
  };

  const unpublishResource = async (resourceId) => {
    // Add loading state
    setPublishingResources(prev => new Set([...prev, resourceId]));
    
    try {
      console.log(`📝 Unpublishing resource: ${resourceId}`);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.put(`${backendUrl}/api/admin/resources/${resourceId}/unpublish`);
      
      console.log('✅ Resource unpublished successfully:', response.data);
      
      showToast('Resource unpublished successfully! It is now hidden from public view.', 'success');
      
      await fetchResources();
      await fetchResourceStats();
      
      return { success: true, message: 'Resource unpublished successfully' };
    } catch (error) {
      console.error('❌ Error unpublishing resource:', error);
      
      let errorMessage = 'Failed to unpublish resource. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      showToast(errorMessage, 'error');
      return { success: false, message: errorMessage };
    } finally {
      // Remove loading state
      setPublishingResources(prev => {
        const newSet = new Set([...prev]);
        newSet.delete(resourceId);
        return newSet;
      });
    }
  };

  const handleFlagReview = async (flagId, action, notes = '') => {
    // Check if flag is already being processed
    if (processingFlags.has(flagId)) {
      debugLog('review', `Flag ${flagId} is already being processed, ignoring duplicate request`);
      showNotification('This flag is already being processed. Please wait...', 'warning');
      return;
    }

    debugLog('review', `Starting flag review: ${action} on flag ${flagId}`, { action, notes });
    
    // Add flag to processing set and update UI
    setProcessingFlags(prev => new Set([...prev, flagId]));
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('action', action);
      if (notes) {
        params.append('notes', notes);
      }
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const url = `${backendUrl}/api/admin/flags/${flagId}/review?${params.toString()}`;
      
      debugLog('review', 'Request details', {
        url: url,
        action: action,
        notes: notes,
        queryParams: params.toString(),
        hasAuthToken: !!localStorage.getItem('token')
      });
      
      const response = await axios.put(url, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      debugLog('review', 'Flag review succeeded', response.data);
      
      // Immediately remove the flag from the UI for instant UX feedback
      setPendingFlags(prevFlags => {
        const updatedFlags = prevFlags.filter(flag => flag.id !== flagId);
        debugLog('review', `Immediately removed flag from UI. Remaining flags: ${updatedFlags.length}`);
        return updatedFlags;
      });
      
      // Show success notification
      const actionText = action === 'dismiss' ? 'dismissed' : 
                        action === 'warn_user' ? 'warned' : 
                        action === 'remove_content' ? 'removed' : 'processed';
      
      showNotification(`✅ Flag ${actionText} successfully!`, 'success');
      
      // Refresh data to ensure consistency (with delay to allow backend processing)
      setTimeout(async () => {
        debugLog('review', 'Refreshing data after successful review');
        await Promise.all([
          fetchPendingFlags(),
          fetchDashboardData()
        ]);
      }, 1000);
      
    } catch (error) {
      debugLog('review', 'Flag review failed', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        errorMessage: error.message
      });
      
      // Show specific error message from backend if available
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message;
      
      if (error.response?.status === 400 && errorMessage.includes('already been reviewed')) {
        showNotification('This flag has already been reviewed. Refreshing the list...', 'warning');
        // Force refresh to sync UI with actual state
        setTimeout(() => fetchPendingFlags(), 500);
      } else {
        showNotification(`❌ Failed to ${action} flag: ${errorMessage}`, 'error');
      }
      
    } finally {
      // Always remove flag from processing set
      setProcessingFlags(prev => {
        const newSet = new Set(prev);
        newSet.delete(flagId);
        return newSet;
      });
      debugLog('review', `Removed flag ${flagId} from processing queue`);
    }
  };

  const handleContentApproval = async (contentType, contentId, action, notes = '') => {
    try {
      const params = new URLSearchParams({ action });
      if (notes) {
        params.append('notes', notes);
      }
      
      const url = `admin/approve-content/${contentType}/${contentId}?${params.toString()}`;
      console.log('🔍 DEBUG: Content approval request details:');
      console.log('   URL:', url);
      console.log('   Content Type:', contentType);
      console.log('   Content ID:', contentId);
      console.log('   Action:', action);
      console.log('   Notes:', notes);
      
      const response = await axios.put(url, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('✅ Content approval succeeded - refreshing data...');
      console.log('   Response:', response.data);
      
      // Refresh content list
      fetchPendingContent();
      fetchDashboardData();
      
      alert(`✅ Content ${action}d successfully!`);
    } catch (error) {
      console.error('🚨 Error approving content:', error);
      console.log('🚨 DEBUG: Content approval failed with:');
      console.log('   Status:', error.response?.status);
      console.log('   Status Text:', error.response?.statusText);
      console.log('   Response Data:', error.response?.data);
      console.log('   Full Error:', error);
      
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message;
      alert(`❌ Failed to ${action} content: ${errorMessage}`);
    }
  };

  // User Management Functions
  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({
        skip: userPagination.skip,
        limit: userPagination.limit,
        active_only: userFilters.active_only
      });
      
      if (userFilters.search) params.append('search', userFilters.search);
      if (userFilters.role) params.append('role', userFilters.role);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    }
  };

  const fetchUserStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/admin/users/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUserStats(response.data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      console.log('👁️ Fetching details for user:', userId);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('✅ User details response:', response.data);
      setShowUserDetails(response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch user details:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to fetch user details';
      showToast(`Failed to fetch user details: ${errorMessage}`, 'error');
      return null;
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.put(`${backendUrl}/api/admin/users/${userId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast(`User role updated successfully!`, 'success');
      fetchUsers(); // Refresh the user list
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update user role';
      showToast(`Failed to update user role: ${errorMessage}`, 'error');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.put(`${backendUrl}/api/admin/users/${userId}/status`, { is_active: newStatus }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast(`User status updated successfully!`, 'success');
      fetchUsers(); // Refresh the user list
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update user status';
      showToast(`Failed to update user status: ${errorMessage}`, 'error');
    }
  };

  const resetUserPassword = async (userId) => {
    try {
      console.log('🔑 Resetting password for user:', userId);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${backendUrl}/api/admin/users/${userId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('✅ Password reset response:', response.data);
      setTempPassword(response.data.temporary_password);
      setShowPasswordReset(userId);
      fetchUsers(); // Refresh the user list
      showToast('Password reset successfully! Temporary password generated.', 'success');
    } catch (error) {
      console.error('❌ Password reset error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to reset password';
      showToast(`Failed to reset password: ${errorMessage}`, 'error');
    }
  };

  const deleteUser = async (userId, permanent = false) => {
    const confirmMessage = permanent 
      ? 'Are you sure you want to PERMANENTLY delete this user? This cannot be undone!'
      : 'Are you sure you want to deactivate this user account?';
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.delete(`${backendUrl}/api/admin/users/${userId}?permanent=${permanent}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast(`User ${permanent ? 'deleted' : 'deactivated'} successfully!`, 'success');
      fetchUsers(); // Refresh the user list
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete user';
      showToast(`Failed to delete user: ${errorMessage}`, 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Modern Header */}
        <div className="mb-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Admin Center
              </h1>
              <p className="mt-3 text-lg text-gray-600">
                Community management and content moderation
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {user?.full_name?.charAt(0) || 'A'}
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-gray-900">{user?.full_name}</p>
                    <p className="text-gray-500 capitalize">{user?.role} Access</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Navigation Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl p-2 shadow-sm border border-gray-100">
            <nav className="flex space-x-2">
              {[
                { id: 'dashboard', label: 'Overview', icon: '📊' },
                { id: 'flags', label: `Flagged Content`, badge: pendingFlags.length, icon: '🚩' },
                { id: 'content', label: `Pending Approval`, badge: pendingContent.length, icon: '⏳' },
                { id: 'resources', label: 'Resources', icon: '📚' },
                { id: 'users', label: 'Users', icon: '👥' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    relative flex items-center space-x-2 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200
                    ${activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }
                  `}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                  {tab.badge > 0 && (
                    <span className={`
                      absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold flex items-center justify-center
                      ${activeTab === tab.id ? 'bg-white text-blue-600' : 'bg-red-500 text-white'}
                    `}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Dashboard Overview */}
        {activeTab === 'dashboard' && dashboardData && (
          <div className="space-y-8">
            {/* Modern Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Total Users"
                value={dashboardData.stats.total_users}
                icon="👥"
                color="blue"
                description="Active community members"
                onClick={() => setActiveTab('users')}
              />
              
              <MetricCard
                title="Pending Flags"
                value={dashboardData.stats.pending_flags}
                icon="🚩"
                color="red"
                description="Requiring immediate attention"
                onClick={() => setActiveTab('flags')}
              />
              
              <MetricCard
                title="Pending Approvals"
                value={dashboardData.stats.pending_approvals}
                icon="⏳"
                color="yellow"
                description="Awaiting admin review"
                onClick={() => setActiveTab('content')}
              />
              
              <MetricCard
                title="Recent Actions"
                value={dashboardData.recent_activities.length}
                icon="⚡"
                color="green"
                description="Completed this week"
              />
            </div>

            {/* Recent Activity */}
            {dashboardData.recent_activities && dashboardData.recent_activities.length > 0 && (
              <Card 
                title="Recent Admin Activities"
                description="Latest moderation actions and system events"
              >
                <div className="space-y-4">
                  {dashboardData.recent_activities.slice(0, 5).map((activity, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 text-sm">
                            {activity.action_type === 'approve' ? '✅' : 
                             activity.action_type === 'reject' ? '❌' : 
                             activity.action_type === 'dismiss' ? '👋' : 
                             activity.action_type ? '⚡' : '❓'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {activity.action_type ? (activity.action_type.charAt(0).toUpperCase() + activity.action_type.slice(1)) : 'Unknown'} {activity.content_type || ''}
                          </p>
                          <p className="text-xs text-gray-500">
                            {activity.notes || 'No additional notes'}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(activity.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Flagged Content Tab */}
        {activeTab === 'flags' && (
          <Card 
            title="Flagged Content"
            description="Review and moderate community-reported content"
            className="space-y-6"
          >
            {pendingFlags.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">✨</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">All Clear!</h3>
                <p className="text-gray-600">No pending flags to review at this time.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingFlags.map((flag) => (
                  <div key={flag.id} className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                            🚩 {flag.reason}
                          </span>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            📄 {flag.content_type}
                          </span>
                        </div>
                        
                        {flag.content && (
                          <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-700">
                              {flag.content.content || flag.content.name || flag.content.title || 'Content preview not available'}
                            </p>
                          </div>
                        )}
                        
                        {flag.description && (
                          <p className="text-sm text-gray-600 mb-3">
                            <span className="font-semibold">Report Details:</span> {flag.description}
                          </p>
                        )}
                        
                        {flag.reporter && (
                          <p className="text-sm text-gray-500">
                            Reported by: <span className="font-medium">{flag.reporter.full_name}</span> ({flag.reporter.email})
                          </p>
                        )}
                      </div>
                      
                      <div className="flex space-x-2 ml-6">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFlagReview(flag.id, 'dismiss')}
                          disabled={processingFlags.has(flag.id)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          👋 Dismiss
                        </Button>
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => handleFlagReview(flag.id, 'warn_user')}
                          disabled={processingFlags.has(flag.id)}
                        >
                          ⚠️ Warn User
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleFlagReview(flag.id, 'remove_content')}
                          disabled={processingFlags.has(flag.id)}
                        >
                          {processingFlags.has(flag.id) ? '⏳ Removing...' : '🗑️ Remove'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Pending Content Tab */}
        {activeTab === 'content' && (
          <Card 
            title="Pending Approval"
            description="Review and approve community submitted content"
            className="space-y-6"
          >
            {pendingContent.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">All Caught Up!</h3>
                <p className="text-gray-600">No content waiting for approval.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingContent.map((content) => (
                  <div key={content.id} className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                            ⏳ {content.content_type}
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            📅 {new Date(content.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">
                          {content.name || content.title}
                        </h3>
                        
                        <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                          {content.description}
                        </p>
                        
                        {content.industry_tags && content.industry_tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {content.industry_tags.map((tag, index) => (
                              <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 font-medium">
                                🏷️ {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-3 ml-6">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleContentApproval(content.content_type, content.id, 'reject')}
                        >
                          ❌ Reject
                        </Button>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleContentApproval(content.content_type, content.id, 'approve')}
                        >
                          ✅ Approve
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Resource Management Tab */}
        {activeTab === 'resources' && (
          <div className="space-y-8">
            {/* Resource Statistics */}
            {resourceStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                  title="Total Resources"
                  value={resourceStats.overview.total_resources}
                  icon="📚"
                  color="blue"
                  description="All created resources"
                />
                
                <MetricCard
                  title="Published"
                  value={resourceStats.overview.published_resources}
                  icon="✅"
                  color="green"
                  description="Live and accessible"
                />
                
                <MetricCard
                  title="Drafts"
                  value={resourceStats.overview.draft_resources}
                  icon="📝"
                  color="yellow"
                  description="Work in progress"
                />
                
                <MetricCard
                  title="Featured"
                  value={resourceStats.overview.featured_resources}
                  icon="⭐"
                  color="purple"
                  description="Highlighted content"
                />
              </div>
            )}

            {/* Resource Management */}
            <Card>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Content Library</h2>
                  <p className="text-gray-600 mt-1">Manage your educational resources and materials</p>
                </div>
                <Button
                  onClick={() => setShowResourceForm(true)}
                  icon="📝"
                  size="lg"
                >
                  Add Resource
                </Button>
              </div>

              {/* Modern Filters */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Select
                    label="Type"
                    value={resourceFilters.resource_type}
                    onChange={(e) => setResourceFilters(prev => ({ ...prev, resource_type: e.target.value }))}
                    options={[
                      { value: '', label: 'All Types' },
                      { value: 'video', label: '📹 Video' },
                      { value: 'article', label: '📄 Article' },
                      { value: 'blog', label: '📝 Blog' },
                      { value: 'ebook', label: '📖 Ebook' }
                    ]}
                  />
                  
                  <Select
                    label="Category"
                    value={resourceFilters.category}
                    onChange={(e) => setResourceFilters(prev => ({ ...prev, category: e.target.value }))}
                    options={[
                      { value: '', label: 'All Categories' },
                      { value: 'Business Management', label: 'Business Management' },
                      { value: 'Legal & Compliance', label: 'Legal & Compliance' },
                      { value: 'Marketing', label: 'Marketing' },
                      { value: 'Finance', label: 'Finance' },
                      { value: 'Procurement', label: 'Procurement' },
                      { value: 'Technology', label: 'Technology' }
                    ]}
                  />
                  
                  <Select
                    label="Status"
                    value={resourceFilters.approval_status}
                    onChange={(e) => setResourceFilters(prev => ({ ...prev, approval_status: e.target.value }))}
                    options={[
                      { value: '', label: 'All Status' },
                      { value: 'approved', label: '✅ Published' },
                      { value: 'draft', label: '📝 Draft' },
                      { value: 'pending', label: '⏳ Pending' }
                    ]}
                  />
                  
                  <Input
                    label="Search"
                    placeholder="Search resources..."
                    value={resourceFilters.search}
                    onChange={(e) => setResourceFilters(prev => ({ ...prev, search: e.target.value }))}
                    icon="🔍"
                  />
                </div>
                
                <div className="mt-6">
                  <Button
                    onClick={fetchResources}
                    variant="secondary"
                    icon="🔍"
                  >
                    Apply Filters
                  </Button>
                </div>
              </div>

              {/* Resources List */}
              <div className="space-y-4">
                {resources.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">📚</span>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No resources found</h3>
                    <p className="text-gray-600">Create your first resource to get started.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {resources.map((resource) => (
                      <div key={resource.id} className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 hover:scale-[1.02]">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              resource.resource_type === 'video' ? 'bg-red-100 text-red-800' :
                              resource.resource_type === 'article' ? 'bg-blue-100 text-blue-800' :
                              resource.resource_type === 'ebook' ? 'bg-purple-100 text-purple-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {resource.resource_type === 'video' ? '📹' : 
                               resource.resource_type === 'article' ? '📄' : 
                               resource.resource_type === 'ebook' ? '📖' : '📝'} {resource.resource_type}
                            </span>
                            {resource.featured && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                ⭐ Featured
                              </span>
                            )}
                          </div>
                          <div className="flex space-x-1">
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => setEditingResource(resource)}
                            >
                              ✏️
                            </Button>
                            <Button
                              size="xs" 
                              variant="ghost"
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to delete this resource? This action will remove it from the public listing.')) {
                                  await deleteResource(resource.id);
                                }
                              }}
                              disabled={deletingResources.has(resource.id)}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              {deletingResources.has(resource.id) ? (
                                <span className="animate-spin">⏳</span>
                              ) : (
                                '🗑️'
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{resource.title}</h3>
                        <p className="text-sm text-gray-600 mb-4 line-clamp-3">{resource.description}</p>
                        
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                          <span>📊 {resource.category}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            resource.approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                            resource.approval_status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {resource.approval_status}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                          <div className="flex items-center space-x-3">
                            <span>👁️ {resource.views_count}</span>
                            <span>❤️ {resource.likes_count}</span>
                          </div>
                          {(resource.duration_minutes || resource.read_time_minutes) && (
                            <span>⏱️ {resource.duration_minutes || resource.read_time_minutes}min</span>
                          )}
                        </div>
                        
                        <div className="text-xs text-gray-500 mb-4">
                          By {resource.author_name} • {new Date(resource.published_date).toLocaleDateString()}
                        </div>
                        
                        {/* Resource Attachments */}
                        {resource.attachments && resource.attachments.length > 0 && (
                          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                            <div className="text-sm text-blue-800 font-medium mb-1">
                              📎 {resource.attachments.length} attachment{resource.attachments.length > 1 ? 's' : ''}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex space-x-2 pt-4 border-t border-gray-100">
                          {resource.approval_status === 'draft' ? (
                            <Button
                              size="sm"
                              variant="success"
                              onClick={async () => await publishResource(resource.id)}
                              disabled={publishingResources.has(resource.id)}
                              className="flex-1"
                            >
                              {publishingResources.has(resource.id) ? (
                                <>
                                  <span className="animate-spin mr-1">⏳</span>
                                  Publishing...
                                </>
                              ) : (
                                '🚀 Publish'
                              )}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="warning"
                              onClick={async () => await unpublishResource(resource.id)}
                              disabled={publishingResources.has(resource.id)}
                              className="flex-1"
                            >
                              {publishingResources.has(resource.id) ? (
                                <>
                                  <span className="animate-spin mr-1">⏳</span>
                                  Unpublishing...
                                </>
                              ) : (
                                '📝 Unpublish'
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-8">
            {/* User Statistics */}
            {userStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                  title={userStats.overview.total_users}
                  label="Total Users"
                  icon="👥"
                  color="blue"
                  description="All registered accounts"
                />
                <MetricCard
                  title={userStats.overview.active_users}
                  label="Active Users"
                  icon="✅"
                  color="green"
                  description="Currently active accounts"
                />
                <MetricCard
                  title={userStats.overview.admin_users}
                  label="Admin Users"
                  icon="👨‍💼"
                  color="purple"
                  description="Users with admin privileges"
                />
                <MetricCard
                  title={userStats.activity.new_registrations_30d}
                  label="New This Month"
                  icon="🆕"
                  color="teal"
                  description="Registrations in last 30 days"
                />
              </div>
            )}

            {/* User Management Controls */}
            <Card
              title="User Management"
              description="Manage user accounts, roles, and permissions"
            >
              {/* Filters and Search */}
              <div className="mb-6 flex flex-wrap gap-4">
                <Input
                  placeholder="Search users by name, email, or username..."
                  value={userFilters.search}
                  onChange={(e) => setUserFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="flex-1 min-w-64"
                />
                <Select
                  value={userFilters.role}
                  onChange={(value) => setUserFilters(prev => ({ ...prev, role: value }))}
                  options={[
                    { value: '', label: 'All Roles' },
                    { value: 'user', label: 'Regular Users' },
                    { value: 'admin', label: 'Administrators' }
                  ]}
                />
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={userFilters.active_only}
                    onChange={(e) => setUserFilters(prev => ({ ...prev, active_only: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Active only</span>
                </label>
              </div>

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Activity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {user.full_name?.charAt(0) || 'U'}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{user.full_name || 'Unknown'}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={user.role || 'user'}
                            onChange={(e) => updateUserRole(user.id, e.target.value)}
                            className={`text-sm rounded-full px-3 py-1 font-semibold border ${
                              user.role === 'admin' 
                                ? 'bg-purple-100 text-purple-800 border-purple-300' 
                                : 'bg-blue-100 text-blue-800 border-blue-300'
                            }`}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleUserStatus(user.id, user.is_active)}
                            className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                              user.is_active
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {user.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div>
                            <div>{user.posts_count || 0} posts</div>
                            <div>{user.groups_count || 0} groups</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => resetUserPassword(user.id)}
                              className="text-blue-600 hover:text-blue-900 font-medium"
                              title="Reset Password"
                            >
                              🔑
                            </button>
                            <button
                              onClick={() => fetchUserDetails(user.id)}
                              className="text-green-600 hover:text-green-900 font-medium"
                              title="View Details"
                            >
                              👁️
                            </button>
                            <button
                              onClick={() => deleteUser(user.id, false)}
                              className="text-red-600 hover:text-red-900 font-medium"
                              title="Deactivate User"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {userPagination.skip + 1} to {Math.min(userPagination.skip + userPagination.limit, users.length)} of {users.length} users
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => setUserPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                    disabled={userPagination.skip === 0}
                    variant="outline"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={() => setUserPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                    disabled={users.length < userPagination.limit}
                    variant="outline"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Enhanced User Details Modal */}
        {showUserDetails && (
          <Modal isOpen={true} onClose={() => setShowUserDetails(null)}>
            <div className="p-6">
              <h3 className="text-xl font-bold mb-6 text-gray-900">👤 User Details</h3>
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{showUserDetails.full_name || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{showUserDetails.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{showUserDetails.username || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
                    <p className={`text-sm font-medium p-3 rounded-lg ${
                      showUserDetails.role === 'admin' ? 'bg-red-100 text-red-800' : 
                      showUserDetails.role === 'moderator' ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {showUserDetails.role || 'user'}
                    </p>
                  </div>
                </div>

                {/* Status and Activity */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account Status</label>
                    <p className={`text-sm font-medium p-3 rounded-lg ${
                      showUserDetails.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {showUserDetails.is_active ? '🟢 Active' : '🔴 Inactive'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Account Age</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                      {showUserDetails.account_age_days || 0} days
                    </p>
                  </div>
                </div>

                {/* Statistics */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">📊 Activity Statistics</h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{showUserDetails.posts_count || 0}</div>
                      <div className="text-xs text-gray-600">Posts</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{showUserDetails.groups_count || 0}</div>
                      <div className="text-xs text-gray-600">Groups</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{showUserDetails.events_count || 0}</div>
                      <div className="text-xs text-gray-600">Events</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">{showUserDetails.resources_count || 0}</div>
                      <div className="text-xs text-gray-600">Resources</div>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                {(showUserDetails.company_name || showUserDetails.location || showUserDetails.phone) && (
                  <div className="grid grid-cols-2 gap-6">
                    {showUserDetails.company_name && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Company</label>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{showUserDetails.company_name}</p>
                      </div>
                    )}
                    {showUserDetails.location && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{showUserDetails.location}</p>
                      </div>
                    )}
                    {showUserDetails.phone && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{showUserDetails.phone}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4 border-t">
                  <Button 
                    onClick={() => {
                      setShowUserDetails(null);
                      resetUserPassword(showUserDetails.id);
                    }}
                    variant="outline"
                    className="flex items-center space-x-2"
                  >
                    <span>🔑</span>
                    <span>Reset Password</span>
                  </Button>
                  <Button 
                    onClick={() => setShowUserDetails(null)}
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </Modal>
        )}

        {/* Enhanced Password Reset Modal */}
        {showPasswordReset && (
          <Modal isOpen={true} onClose={() => setShowPasswordReset(null)}>
            <div className="p-6">
              <h3 className="text-xl font-bold mb-6 text-gray-900">🔑 Password Reset Successful</h3>
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className="text-green-500 text-xl mr-2">✅</span>
                    <span className="font-semibold text-green-800">Password Reset Completed</span>
                  </div>
                  <p className="text-sm text-green-700">
                    A secure temporary password has been generated for the user.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Temporary Password:
                  </label>
                  <div className="bg-gray-900 p-4 rounded-lg border-2 border-blue-200">
                    <code className="text-2xl font-mono text-green-400 tracking-wider">{tempPassword}</code>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <div className="flex items-start">
                    <span className="text-yellow-500 text-xl mr-2 mt-0.5">⚠️</span>
                    <div>
                      <h4 className="font-semibold text-yellow-800 mb-2">Important Instructions:</h4>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        <li>• Securely share this password with the user</li>
                        <li>• User must change password on next login</li>
                        <li>• This password expires after 24 hours</li>
                        <li>• Do not share via unencrypted channels</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  <Button 
                    onClick={() => {
                      navigator.clipboard.writeText(tempPassword);
                      showToast('Temporary password copied to clipboard!', 'success');
                    }}
                    variant="outline"
                    className="flex items-center space-x-2"
                  >
                    <span>📋</span>
                    <span>Copy Password</span>
                  </Button>
                  <Button 
                    onClick={() => setShowPasswordReset(null)}
                    className="flex-1"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </Modal>
        )}

        {/* Resource Form Modal */}
        {showResourceForm && (
          <ResourceFormModal
            onClose={() => setShowResourceForm(false)}
            onSubmit={createResource}
          />
        )}

        {/* Resource Edit Modal */}
        {editingResource && (
          <ResourceFormModal
            resource={editingResource}
            onClose={() => setEditingResource(null)}
            onSubmit={(data) => updateResource(editingResource.id, data)}
          />
        )}

        {/* Toast Notification */}
        {toast && (
          <div className={`
            fixed top-4 right-4 z-50 max-w-md p-4 rounded-lg shadow-lg border-l-4 transition-all duration-300 transform
            ${toast.type === 'success' ? 'bg-green-50 border-green-400 text-green-800' : 
              toast.type === 'error' ? 'bg-red-50 border-red-400 text-red-800' :
              'bg-blue-50 border-blue-400 text-blue-800'}
          `}
          style={{
            animation: 'slideInFromRight 0.3s ease-out'
          }}
          >
            <style>{`
              @keyframes slideInFromRight {
                from {
                  transform: translateX(100%);
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}</style>
            <div className="flex items-start">
              <div className="flex-1">
                <div className="flex items-center">
                  <span className="text-lg mr-2">
                    {toast.type === 'success' ? '✅' : 
                     toast.type === 'error' ? '❌' : 'ℹ️'}
                  </span>
                  <span className="font-medium">
                    {toast.type === 'success' ? 'Success' : 
                     toast.type === 'error' ? 'Error' : 'Info'}
                  </span>
                </div>
                <p className="mt-1 text-sm">{toast.message}</p>
              </div>
              <button
                onClick={() => setToast(null)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Resource Form Modal Component
const ResourceFormModal = ({ resource, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: resource?.title || '',
    description: resource?.description || '',
    resource_type: resource?.resource_type || 'article',
    category: resource?.category || 'Business Management',
    author_name: resource?.author_name || '',
    author_organization: resource?.author_organization || '',
    content_url: resource?.content_url || '',
    thumbnail_url: resource?.thumbnail_url || '',
    duration_minutes: resource?.duration_minutes || '',
    read_time_minutes: resource?.read_time_minutes || '',
    tags: resource?.tags?.join(', ') || '',
    featured: resource?.featured || false,
    priority: resource?.priority || 0,
    draft_mode: resource?.approval_status === 'draft' || false
  });
  const [loading, setLoading] = useState(false);
  
  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadingFiles(true);

    try {
      // Upload files first if any are selected
      let attachmentIds = [...(resource?.attachments || [])]; // Keep existing attachments
      if (selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          formDataUpload.append('category', 'resources');
          
          setFileUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
          
          const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
          const uploadResponse = await axios.post(`${backendUrl}/api/upload`, formDataUpload, {
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (uploadResponse.data.file) {
            attachmentIds.push(uploadResponse.data.file.id);
          }
        }
      }

      const submitData = {
        ...formData,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        read_time_minutes: formData.read_time_minutes ? parseInt(formData.read_time_minutes) : null,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        priority: parseInt(formData.priority) || 0,
        attachments: attachmentIds
      };

      const result = await onSubmit(submitData);
      
      if (result?.success) {
        onClose();
      } else {
        alert(result?.message || 'Failed to save resource');
      }
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Failed to save resource. Please try again.');
    } finally {
      setLoading(false);
      setUploadingFiles(false);
    }
  };

  // File upload functions
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    
    for (const file of files) {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      const isDocument = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain'
      ].includes(file.type);
      
      if (!isImage && !isDocument) {
        alert(`File "${file.name}" is not supported. Please upload images or documents only.`);
        continue;
      }
      
      // Validate file size (10MB for images, 25MB for documents)
      const maxSize = isImage ? 10 * 1024 * 1024 : 25 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxMB = maxSize / (1024 * 1024);
        alert(`File "${file.name}" is too large. Maximum size is ${maxMB}MB.`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto relative z-[10000]">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {resource ? 'Edit Resource' : 'Create New Resource'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select
                required
                value={formData.resource_type}
                onChange={(e) => setFormData(prev => ({ ...prev, resource_type: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 relative z-[10001]"
              >
                <option value="video">📹 Video</option>
                <option value="article">📄 Article</option>
                <option value="blog">📝 Blog</option>
                <option value="ebook">📖 Ebook</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              required
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 relative z-[10001]"
              >
                <option value="Business Management">Business Management</option>
                <option value="Legal & Compliance">Legal & Compliance</option>
                <option value="Marketing">Marketing</option>
                <option value="Finance">Finance</option>
                <option value="Procurement">Procurement</option>
                <option value="Technology">Technology</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Author Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Author Name *</label>
              <input
                type="text"
                required
                value={formData.author_name}
                onChange={(e) => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Author Organization *</label>
              <input
                type="text"
                required
                value={formData.author_organization}
                onChange={(e) => setFormData(prev => ({ ...prev, author_organization: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Content URLs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content URL</label>
              <input
                type="url"
                value={formData.content_url}
                onChange={(e) => setFormData(prev => ({ ...prev, content_url: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail URL</label>
              <input
                type="url"
                value={formData.thumbnail_url}
                onChange={(e) => setFormData(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Duration/Reading Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.resource_type === 'video' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {(formData.resource_type === 'article' || formData.resource_type === 'blog' || formData.resource_type === 'ebook') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Read Time (minutes)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.read_time_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, read_time_minutes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="e.g. business, finance, management"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* File Upload Section */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">📎 File Attachments</h4>
            
            {/* File Upload Button */}
            <div className="flex items-center space-x-4 mb-4">
              <input
                type="file"
                id="resource-file-upload"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
              <label
                htmlFor="resource-file-upload"
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 cursor-pointer transition-colors px-3 py-2 rounded-lg hover:bg-gray-100 border border-gray-300"
              >
                <span>📎</span>
                <span>Attach Files</span>
              </label>
              <label
                htmlFor="resource-file-upload"
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 cursor-pointer transition-colors px-3 py-2 rounded-lg hover:bg-gray-100 border border-gray-300"
              >
                <span>📷</span>
                <span>Images</span>
              </label>
            </div>
            
            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2 mb-4">
                <h5 className="text-sm font-medium text-gray-700">New Attachments:</h5>
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">
                          {file.type.startsWith('image/') ? '🖼️' : 
                           file.type === 'application/pdf' ? '📄' : 
                           file.type.includes('word') ? '📝' : 
                           file.type.includes('excel') ? '📊' :
                           file.type.includes('powerpoint') ? '📊' : '📄'}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        ❌
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Existing Attachments */}
            {resource?.attachments && resource.attachments.length > 0 && (
              <div className="space-y-2 mb-4">
                <h5 className="text-sm font-medium text-gray-700">Existing Attachments:</h5>
                <div className="space-y-2">
                  {resource.attachments.map((attachmentId, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-2">
                      <FileAttachmentDisplay attachmentId={attachmentId} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Upload Progress */}
            {uploadingFiles && (
              <div className="mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm text-gray-600">Uploading files...</span>
                  <span className="text-sm text-blue-600">{fileUploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${fileUploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                ⭐ Featured Resource
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="draft_mode"
                checked={formData.draft_mode}
                onChange={(e) => setFormData(prev => ({ ...prev, draft_mode: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="draft_mode" className="ml-2 block text-sm text-gray-900">
                📝 Save as Draft
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingFiles}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {uploadingFiles ? 'Uploading files...' : 
               loading ? 'Saving...' : 
               (resource ? 'Update Resource' : 'Create Resource')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// File Attachment Display Component for Admin Dashboard
const FileAttachmentDisplay = ({ attachmentId }) => {
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFileInfo();
  }, [attachmentId]);

  const fetchFileInfo = async () => {
    try {
      const response = await axios.get(`/files/${attachmentId}/info`);
      setFileInfo(response.data);
    } catch (error) {
      console.error('Error fetching file info:', error);
      setError('Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (fileInfo) {
      const downloadUrl = `/files/${fileInfo.category}/${fileInfo.filename}`;
      window.open(downloadUrl, '_blank');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-3 p-2 animate-pulse">
        <div className="w-8 h-8 bg-gray-300 rounded"></div>
        <div className="flex-1">
          <div className="h-3 bg-gray-300 rounded w-3/4 mb-1"></div>
          <div className="h-2 bg-gray-300 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center space-x-3 p-2 text-red-600">
        <span className="text-lg">❌</span>
        <span className="text-sm">{error}</span>
      </div>
    );
  }

  if (!fileInfo) return null;

  const isImage = fileInfo.file_type === 'image';
  const fileUrl = `/files/${fileInfo.category}/${fileInfo.filename}`;

  return (
    <div className="flex items-center justify-between p-2">
      <div className="flex items-center space-x-3 flex-1">
        {isImage ? (
          <img
            src={fileUrl}
            alt={fileInfo.original_filename}
            className="w-10 h-10 object-cover rounded"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : (
          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
            <span className="text-lg">
              {fileInfo.extension === '.pdf' ? '📄' :
               fileInfo.extension?.includes('.doc') ? '📝' :
               fileInfo.extension?.includes('.xls') ? '📊' :
               fileInfo.extension?.includes('.ppt') ? '📊' :
               fileInfo.extension === '.txt' ? '📄' : '📁'}
            </span>
          </div>
        )}
        
        {/* Hidden fallback for failed images */}
        <div 
          className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center"
          style={{ display: 'none' }}
        >
          <span className="text-lg">🖼️</span>
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {fileInfo.original_filename}
          </p>
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <span>{formatFileSize(fileInfo.file_size)}</span>
            <span>•</span>
            <span className="capitalize">{fileInfo.file_type}</span>
          </div>
        </div>
      </div>
      
      <button
        onClick={handleDownload}
        className="flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
      >
        <span>📥</span>
        <span>{isImage ? 'View' : 'Download'}</span>
      </button>
    </div>
  );
};

export default AdminDashboard;